import React, { useEffect } from "react";
import { z } from "zod";

import { ButtonStandard, Text } from "@performyard/py-components";

import { Grid, GridCol } from "../../../../../components/Grid";
import SelectInput from "../../../../../components/Inputs/SelectInput";
import Spacer from "../../../../../components/Spacer";
import Spinner from "../../../../../components/Spinner/ComponentLoader";

import { useCompanySettings, useUpdateCompanySessionSettings } from "../../../../../api/hooks/defs/company";
import useForm from "../../../../../hooks/useForm";

import { getSessionTimeoutOptions, sessionTimeoutRedirectOptions } from "./config";

// Form schema for validation
const formSchema = z.object({
	sessionTimeout: z.number(),
	sessionTimeoutRedirect: z.string(),
});

/**
 * Company Settings component for managing session timeout configurations.
 */
const CompanySettings: React.FC = () => {
	const { data: companySettings } = useCompanySettings();
	const { mutate: updateSessionSettings, isPending } = useUpdateCompanySessionSettings();

	const sessionTimeoutOptions = getSessionTimeoutOptions();

	// Initialize form with default values
	const { state, updateForm, resetForm, hasUnsavedChanges, setNewDefaultValues } = useForm({
		schema: formSchema,
		defaultValues: {
			sessionTimeout: 720,
			sessionTimeoutRedirect: "off",
		},
	});

	// Update form when company settings load
	useEffect(() => {
		if (companySettings?.authentication?.sessionTimeout) {
			const newValues = {
				sessionTimeout: companySettings.authentication.sessionTimeout,
				sessionTimeoutRedirect: companySettings.authentication.sessionTimeoutRedirect || "off",
			};
			setNewDefaultValues(newValues);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [companySettings?.authentication?.sessionTimeout, companySettings?.authentication?.sessionTimeoutRedirect]);

	/**
	 * Handles session timeout dropdown changes
	 */
	const handleSessionTimeoutChange = (selectedOption: { value: string; label: string }) => {
		const timeoutValue = parseInt(selectedOption.value, 10);
		updateForm({ sessionTimeout: timeoutValue });
	};

	/**
	 * Handles session timeout redirect dropdown changes
	 */
	const handleRedirectSettingChange = (selectedOption: { value: string; label: string }) => {
		updateForm({ sessionTimeoutRedirect: selectedOption.value });
	};

	/**
	 * Saves all changes
	 */
	const handleSaveChanges = () => {
		updateSessionSettings({
			sessions: {
				timeout: state.sessionTimeout,
				redirect: state.sessionTimeoutRedirect,
			},
		});
	};

	if (!companySettings) {
		return <Spinner size={30} />;
	}

	return (
		<div className="mt-8">
			<Grid cols={10} gap={2}>
				<GridCol span={4}>
					<Text size="paragraph-2" weight="semi-bold" color="neutral-9">
						Session Settings
					</Text>
					<Spacer height={1} />
					<Text size="paragraph-3" weight="regular" color="neutral-9">
						Control how PerformYard should behave when it comes to user sessions.
					</Text>
				</GridCol>
				<GridCol span={1} />
				<GridCol span={5}>
					{/* Session Timeout Setting */}
					<div className="mb-8 flex w-full items-start justify-between space-x-6">
						<div>
							<Text size="paragraph-2" weight="semi-bold" color="neutral-9">
								Session Timeout
							</Text>
							<Spacer height={1} />
							<Text size="paragraph-3" weight="regular" color="neutral-9">
								Select how long an inactive user is given before needing to sign in again.
							</Text>
						</div>
						<div className="w-[200px] flex-shrink-0">
							<div data-testid="sessionSelect" className="w-full">
								<SelectInput
									onChange={handleSessionTimeoutChange}
									value={state.sessionTimeout.toString()}
									results={sessionTimeoutOptions}
								/>
							</div>
						</div>
					</div>

					{/* Session Timeout Redirect Setting */}
					<div className="mb-8 flex w-full items-start justify-between space-x-6">
						<div>
							<Text size="paragraph-2" weight="semi-bold" color="neutral-9">
								Session Timeout Redirect
							</Text>
							<Spacer height={1} />
							<Text size="paragraph-3" weight="regular" color="neutral-9">
								Select whether an inactive user should be automatically redirected to the login page 5
								minutes after session timeout.
							</Text>
						</div>
						<div className="w-[200px] flex-shrink-0">
							<div data-testid="redirectSelect" className="w-full">
								<SelectInput
									onChange={handleRedirectSettingChange}
									value={state.sessionTimeoutRedirect}
									results={sessionTimeoutRedirectOptions}
								/>
							</div>
						</div>
					</div>

					{/* Action Buttons */}
					<div className="flex justify-end space-x-2">
						{/* Discard Button - Only visible when there are unsaved changes and not loading */}
						{hasUnsavedChanges && !isPending && (
							<div>
								<ButtonStandard variant="outline" color="neutral" onClick={resetForm}>
									Discard
								</ButtonStandard>
							</div>
						)}
						{/* Save Changes Button - Always visible */}
						<div>
							<ButtonStandard
								variant="solid"
								color="green"
								onClick={handleSaveChanges}
								disabled={!hasUnsavedChanges || isPending}
							>
								Save Changes
							</ButtonStandard>
						</div>
					</div>
				</GridCol>
			</Grid>
		</div>
	);
};

export default CompanySettings;
